import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DentalScreening, HealthCondition, HealthConditionAttachment, HealthConditionCode, HealthRestriction, HealthVisit, HealthVisitMedications, HealthVisitSearchParam, HearingScreening, ImmunizationExemptions, PhysicalScreening, PhysiciansAttachment, PractitionersAndInsurances, Pregnancy, PregnancyAttachment, RestrictionAttachment, SpinalScreening, StudentHealthNotes, Vaccine, VisionScreening } from './student-health-shared.model';
import { System } from '../../shared-components/System';
import { Observable } from 'rxjs';
import { CustomizationFrameworkService } from '../customization-framework/customization-framework.service';
import { DataDefinition } from '../../admin360/data-definition/data-definition.model';
import { MedicationToday } from '../../health360/medications-today/medications-today.model';
import { map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StudentHealthSharedService {

  healthVisitSearchParam: HealthVisitSearchParam;
  constructor(
    private http: HttpClient,
    private customizationFrameworkService: CustomizationFrameworkService
  ) { }

  getHealthConditionReadByStudentId(studentId: number, siteId: number, yearId: number): Observable<HealthCondition[]> {
    return this.http.get<HealthCondition[]>(System.apiURL.Health + `GetHealthConditionReadByStudentId?studentId=${studentId}&siteId=${siteId}&yearId=${yearId}`);
  }

  deleteHealthConditionById(healthConditionId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteHealthConditionById?healthConditionId=${healthConditionId}`);
  }
  getPregnancyReadByStudentId(studentId: number, siteId: number, yearId: number): Observable<Pregnancy[]> {
    return this.http.get<Pregnancy[]>(System.apiURL.Health + `GetPregnancyReadByStudentId?studentId=${studentId}&siteId=${siteId}&yearId=${yearId}`);
  }

  deletePregnancyById(pregnancyId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeletePregnancyById?pregnancyId=${pregnancyId}`);
  }

  getLookUpValuesByEnum(enum_value: string, yearId: number, siteYearId: number, masterId: number): Observable<DataDefinition[]> {
    return this.customizationFrameworkService.getDataDefinition(enum_value, yearId, siteYearId, masterId);
  }

  getHealthConditionCode(siteId: number, yearId: number): Observable<HealthConditionCode[]> {
    return this.http.get<HealthConditionCode[]>(System.apiURL.Health + `HealthConditionCodes?siteId=${siteId}&yearId=${yearId}`);
  }

  saveHealthConditions(studentId: number, siteYearId: number, healthCondition: HealthCondition): Observable<boolean> {
    const queryString = '?studentId=' + studentId +
      '&siteYearId=' + siteYearId;
    return this.http.post<boolean>(System.apiURL.Health + 'SaveHealthConditions' + queryString, healthCondition);
  }

  savePregnancy(studentId: number, siterYearId: number, data: Pregnancy): Observable<void> {
    return this.http.post<void>(System.apiURL.Health + `SavePregnancy?studentId=${studentId}&siterYearId=${siterYearId}`, data);
  }

  getHealthRestrictionReadByStudentId(studentId: number, siteId: number, yearId: number): Observable<HealthRestriction[]> {
    return this.http.get<HealthRestriction[]>(System.apiURL.Health + `GetHealthRestrictionByStudentId?studentId=${studentId}&siteId=${siteId}&yearId=${yearId}`);
  }

  saveHealthRestriction(studentId: number, siteYearId: number, healthRestriction: HealthRestriction): Observable<boolean> {
    const queryString = '?studentId=' + studentId +
      '&siteYearId=' + siteYearId;
    return this.http.post<boolean>(System.apiURL.Health + 'SaveHealthRestriction' + queryString, healthRestriction);
  }

  getHealthRestrictionAttachementsById(healthRestrictionId: number, siteId: number, yearId: number): Observable<RestrictionAttachment[]> {
    return this.http.get<RestrictionAttachment[]>(System.apiURL.Health + `GetHealthRestrictionAttachementsById?healthRestrictionId=${healthRestrictionId}&siteId=${siteId}&yearId=${yearId}`);
  }

  deleteHealthRestrictionById(healthRestrictionId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteHealthRestrictionById?restrictionId=${healthRestrictionId}`);
  }

  getHealthConditionAttachementsById(healthConditionId: number, siteId: number, yearId: number): Observable<HealthConditionAttachment[]> {
    return this.http.get<HealthConditionAttachment[]>(System.apiURL.Health + `GetHealthConditionAttachementsById?healthConditionId=${healthConditionId}&siteId=${siteId}&yearId=${yearId}`);
  }
  getPregnancyAttachementsById(pregnancyId: number): Observable<PregnancyAttachment[]> {
    return this.http.get<PregnancyAttachment[]>(System.apiURL.Health + `GetPregnancyAttachementsById?pregnancyId=${pregnancyId}`);
  }

  getPractitionersAndInsuranceReadByStudentId(studentId: number, siteId: number, yearId: number, type: string): Observable<PractitionersAndInsurances[]> {
    return this.http.get<PractitionersAndInsurances[]>(System.apiURL.Health + `GetPractitionersAndInsuranceReadByStudentId?studentId=${studentId}&siteId=${siteId}&yearId=${yearId}&type=${type}`);
  }

  deletePractitionerAndInsuranceById(practitionerId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeletePractitionerAndInsuranceById?practitionerId=${practitionerId}`);
  }

  getHealthConditionReadByStudentIdForAlert(studentId: number, siteId: number, yearId: number): Observable<HealthCondition[]> {
    return this.http.get<HealthCondition[]>(System.apiURL.Health + `GetHealthConditionReadByStudentIdForAlert?studentId=${studentId}&siteId=${siteId}&yearId=${yearId}`);
  }

  getHealthRestrictionReadByStudentIdForAlert(studentId: number, siteId: number, yearId: number): Observable<HealthRestriction[]> {
    return this.http.get<HealthRestriction[]>(System.apiURL.Health + `GetHealthRestrictionByStudentIdForAlert?studentId=${studentId}&siteId=${siteId}&yearId=${yearId}`);
  }

  getHealthVisitsReadByStudentId(studentId: number, siteId: number, yearId: number): Observable<HealthVisit[]> {
    return this.http.get<HealthVisit[]>(System.apiURL.Health + `GetHealthVisitsReadByStudentId?studentId=${studentId}&siteId=${siteId}&yearId=${yearId}`);
  }

  deleteHealthVisitById(healthVisitId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteHealthVisitById?healthVisitId=${healthVisitId}`);
  }

  // Save Insurance
  savePractitionersAndInsurances(studentId: number, healthInsuranceData: PractitionersAndInsurances): Observable<boolean> {
    const queryString = '?studentId=' + studentId;
    return this.http.post<boolean>(System.apiURL.Health + 'SavePractitionersAndInsurances' + queryString, healthInsuranceData);
  }

  getPhysiciansAttachementsById(physiciansId: number, siteId: number, yearId: number): Observable<PhysiciansAttachment[]> {
    return this.http.get<PhysiciansAttachment[]>(System.apiURL.Health + `GetPhysiciansAttachementsById?physiciansId=${physiciansId}&siteId=${siteId}&yearId=${yearId}`);
  }

  getStudentHealthNotes(studentId: number, type: string): Observable<StudentHealthNotes> {
    return this.http.get<StudentHealthNotes>(System.apiURL.Health + `GetStudentHealthNotes?studentId=${studentId}&type=${type}`);
  }

  saveHealthNotes(noteType: string, data: StudentHealthNotes): Observable<boolean> {
    return this.http.post<boolean>(System.apiURL.Health + `SaveStudentHealthNotes?noteType=${noteType}`, data);
  }

  saveHealthVisit(studentId: number, siterYearId: number, data: HealthVisit): Observable<boolean> {
    return this.http.post<boolean>(System.apiURL.Health + `SaveHealthVisit?studentId=${studentId}&siterYearId=${siterYearId}`, data);
  }

  getHealthVisitMedicationsReadByStudentId(studentId: number, siteYearId: number, clinicLogId: number): Observable<HealthVisitMedications[]> {
    return this.http.get<HealthVisitMedications[]>(System.apiURL.Health + `GetHealthVisitMedicationsReadByStudentId?studentId=${studentId}&siteYearId=${siteYearId}&clinicLogId=${clinicLogId}`);
  }

  getMedicalMedicationReadByStudentId(studentId: number, siteId: number, YearId: number): Observable<MedicationToday[]> {
    return this.http.get<MedicationToday[]>(System.apiURL.Health + `GetMedicalMedicationReadByStudentId?studentId=${studentId}&siteId=${siteId}&YearId=${YearId}`);
  }

  deleteMedicationById(medicationId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteMedicationById?medicationId=${medicationId}`);
  }

  getMedicationDoseReadByStudentId(studentId: number, siteYearId: number): Observable<MedicationToday[]> {
    return this.http.get<MedicationToday[]>(System.apiURL.Health + `GetMedicationDoseReadByStudentId?studentId=${studentId}&siteYearId=${siteYearId}`);
  }

  getMedicationDoseHistory(siteYearId: number, studentId: number, medicationId: number, fromDate: string, toDate: string): Observable<MedicationToday[]> {
    const queryString = '?siteYearId=' + siteYearId + '&studentId=' + studentId + '&medicationId=' + medicationId
      + '&fromDate=' + fromDate + '&toDate=' + toDate
      ;
    return this.http.get<MedicationToday[]>(System.apiURL.Health + 'GetMedicationDoseHistoryReadByFromDateToDate' + queryString);
  }

  deleteDoseHistory(healthVisitId: number, medicationId: number, isDeleteVisit: boolean): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteDoseHistoryById?healthVisitId=${healthVisitId}&medicationId=${medicationId}&isDeleteVisit=${isDeleteVisit}`);
  }

  saveMassAssignMedication(siteYearId: number, data: MedicationToday[]): Observable<boolean> {
    return this.http.post<boolean>(System.apiURL.Health + `SaveMassAssignMedication?siteYearId=${siteYearId}`, data);
  }

  deleteMedicationInventoryById(inventoryId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteMedicationInventoryById?inventoryId=${inventoryId}`);
  }

  // Screening Methods..
  getHearingScreeningReadByStudentId(studentId: number, siteId: number, YearId: number): Observable<HearingScreening[]> {
    return this.http.get<HearingScreening[]>(System.apiURL.Health + `GetHearingScreeningReadByStudentId?studentId=${studentId}&siteId=${siteId}&YearId=${YearId}`);
  }

  saveHearingScreening(siteYearId: number, data: HearingScreening): Observable<HearingScreening> {
    return this.http.post<HearingScreening>(System.apiURL.Health + `SaveHearingScreening?siteYearId=${siteYearId}`, data);
  }

  deleteHearingScreeningById(hearingScreeningId: number, siteYearId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteHearingScreeningById?hearingScreeningId=${hearingScreeningId}&siteYearId=${siteYearId}`);
  }

  getSpinalScreeningReadByStudentId(studentId: number, siteId: number, YearId: number): Observable<SpinalScreening[]> {
    return this.http.get<SpinalScreening[]>(System.apiURL.Health + `GetSpinalScreeningReadByStudentId?studentId=${studentId}&siteId=${siteId}&YearId=${YearId}`);
  }

  saveSpinalScreening(siteYearId: number, data: SpinalScreening): Observable<SpinalScreening> {
    return this.http.post<SpinalScreening>(System.apiURL.Health + `SaveSpinalScreening?siteYearId=${siteYearId}`, data);
  }

  deleteSpinalScreeningById(spinalScreeningId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteSpinalScreeningById?spinalScreeningId=${spinalScreeningId}`);
  }

  getPhysicalScreeningReadByStudentId(studentId: number, siteId: number, YearId: number): Observable<PhysicalScreening[]> {
    return this.http.get<PhysicalScreening[]>(System.apiURL.Health + `GetPhysicalScreeningReadByStudentId?studentId=${studentId}&siteId=${siteId}&YearId=${YearId}`);
  }

  savePhysicalScreening(siteYearId: number, data: PhysicalScreening): Observable<PhysicalScreening> {
    return this.http.post<PhysicalScreening>(System.apiURL.Health + `SavePhysicalScreening?siteYearId=${siteYearId}`, data);
  }

  deletePhysicalScreeningById(physicalScreeningId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeletePhysicalScreeningById?physicalScreeningId=${physicalScreeningId}`);
  }

  getDentalScreeningReadByStudentId(studentId: number, siteId: number, YearId: number): Observable<DentalScreening[]> {
    return this.http.get<DentalScreening[]>(System.apiURL.Health + `GetDentalScreeningReadByStudentId?studentId=${studentId}&siteId=${siteId}&YearId=${YearId}`);
  }

  saveDentalScreening(siteYearId: number, data: DentalScreening): Observable<DentalScreening> {
    return this.http.post<DentalScreening>(System.apiURL.Health + `SaveDentalScreening?siteYearId=${siteYearId}`, data);
  }

  deleteDentalScreeningById(dentalScreeningId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteDentalScreeningById?dentalScreeningId=${dentalScreeningId}`);
  }

  getVisionScreeningReadByStudentId(studentId: number, siteId: number, yearId: number): Observable<VisionScreening[]> {
    return this.http.get<VisionScreening[]>(System.apiURL.Health + `GetVisionScreeningReadByStudentId?studentId=${studentId}&siteId=${siteId}&yearId=${yearId}`);
  }

  saveVisionScreening(siteYearId: number, data: VisionScreening): Observable<VisionScreening> {
    return this.http.post<VisionScreening>(System.apiURL.Health + `SaveVisionScreening?siteYearId=${siteYearId}`, data);
  }

  deleteVisionScreeningById(visionScreeningId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteVisionScreeningById?visionScreeningId=${visionScreeningId}`);
  }

  getImmunizationExemptions(peopleId: number): Observable<ImmunizationExemptions[]> {
    return this.http.get<ImmunizationExemptions[]>(System.apiURL.Health + `GetImmunizationExemptions?peopleId=${peopleId}`);
  }

  getImmunizationsReadByStudentId(studentId: number, siteId: number, YearId: number): Observable<Vaccine[]> {
    return this.http.get<Vaccine[]>
      (System.apiURL.Health + `GetImmunizationsReadByStudentId?studentId=${studentId}&siteId=${siteId}&YearId=${YearId}`)
      .pipe(
        take(1),
        map(rows => {
          for (let i = 0; i < rows.length; i++) {
            rows[i] = System.convert<Vaccine>(rows[i], new Vaccine(true));
          }
          return rows;
        })
      );
  }

  saveImmunizations(siteYearId: number, studentId: number, data: Vaccine[]): Observable<boolean> {
    return this.http.post<boolean>(System.apiURL.Health + `SaveImmunizations?siteYearId=${siteYearId}&studentId=${studentId}`, data);
  }

  deleteImmunizationById(immunizationId: number): Observable<boolean> {
    return this.http.get<boolean>(System.apiURL.Health + `DeleteImmunizationById?immunizationId=${immunizationId}`);
  }
}
